import { useEffect, useRef, useState } from 'react'
import type { RefObject } from 'react'
import {
  disableBodyScroll,
  enableBodyScroll,
  clearAllBodyScrollLocks,
} from 'body-scroll-lock'
import { ButtonLink, IconLogin } from '@n26/nxd'
import { useLocation, useMatches } from '@remix-run/react'
import { LocaleSwitcherMenuDropdown } from '@components/LocaleSwitcherMenuDropdown'
import { useLocaliseString } from '@hooks/useLocaliseString'
import { LogoLink } from '@components/LogoLink'
import { SignupButton } from '@components/SignupButton'
import { Hamburger } from '@components/Hamburger'
import { Link } from '@components/Link'
import { NavigationMenu } from '@components/NavigationMenu'
import { Accordion } from '@components/Accordion'
import { AccountsNavigationMenu } from '@components/AccountsNavigationMenu'
import type { HeaderLinks } from '@cms/data/getHeaderData'
import type { AccordionGroupProps } from '@components/Accordion'
import { useGetLocale } from '@hooks/useGetLocale'
import { DEFAULT_LOCALE } from '@constants/i18n'
import { trackGTM } from '@helpers/core/trackGTM'
import { WebsiteModuleContext } from '@contexts/WebsiteModuleContext'
import { useEventTracker } from '@hooks/useEventTracker'
import type { LandingPageData, ModifiedQRCodeCTA } from '@pages/LandingPage'
import { LandingPageBanner } from '@components/LandingPageBanner'
import { ProgressBar } from '@components/ProgressBar'

export const HEADER_SNOWPLOW_NAVIGATION_CLICK = 'NAVIGATION_CLICK'

type AllowedLoginButtonVariants = 'secondary' | 'tertiary'

type HeaderProps = {
  headerData: HeaderLinks[]
  bankingUrl: string
  registrationUrl: string
  pageCtaLink?: LandingPageData['landingPageData']['ctaLink']
  qrCodeCta?: ModifiedQRCodeCTA
  banner?: LandingPageData['landingPageData']['banner']
  trackingName?: string | null
  productPreference?: string | null
}

type MinimalHeaderProps = Pick<HeaderProps, 'banner'>

const handleClick = (href: string, loginButtonText?: string) => {
  trackGTM({
    event: 'login_click',
    // eslint-disable-next-line camelcase
    destination_url: href,
    // eslint-disable-next-line camelcase
    navigation_text: loginButtonText,
  })
}

const LoginButton = ({
  href,
  variant = 'tertiary',
}: {
  href: string
  variant?: AllowedLoginButtonVariants
}) => {
  const localiseString = useLocaliseString()
  const loginButtonText = localiseString('loginButtonLabel')
  const { track } = useEventTracker()

  return (
    <ButtonLink
      href={href}
      variant={variant}
      size='small'
      onClick={() => {
        handleClick(href, loginButtonText)
        track({
          action: 'website.login.clicked',
          label: href,
        })
      }}
    >
      {loginButtonText}
    </ButtonLink>
  )
}

type MobileMenuProps = {
  headerRef: RefObject<HTMLElement>
  navRef: RefObject<HTMLElement>
  headerData: HeaderLinks[]
  bankingUrl: string
  registrationUrl: string
}

const MobileMenu = ({
  headerRef,
  navRef,
  headerData,
  bankingUrl,
  registrationUrl,
}: MobileMenuProps) => {
  const [menuHeight, setMenuHeight] = useState('100vh')
  const [headerHeight, setHeaderHeight] = useState(80) // default header height prevents UI flicker
  const localiseString = useLocaliseString()

  useEffect(() => {
    if (headerRef.current) {
      const measuredHeaderHeight = headerRef.current.offsetHeight
      setHeaderHeight(measuredHeaderHeight)
      setMenuHeight(`calc(100dvh - ${measuredHeaderHeight}px)`)
    }
  }, [headerRef])

  const accounts = headerData.length
    ? headerData.filter(item => item.personalTabName && item.businessTabName)[0]
    : null

  const headerLinks: AccordionGroupProps[] = headerData.length
    ? headerData
        .filter(item => Boolean(item.label) && Boolean(item.links))
        .map(item => ({
          label: item.label,
          links:
            item.links?.map(link => ({
              ...link,
              snowplowTrackingData: {
                action: HEADER_SNOWPLOW_NAVIGATION_CLICK,
                label: link.href,
              },
            })) || [],
        }))
    : []

  return (
    <nav
      style={{ height: menuHeight, top: headerHeight }}
      className='absolute left-0 px-12 flex flex-col justify-between bg-neutral w-[100vw] max-w-[100vw] lg:hidden overflow-y-scroll'
      ref={navRef}
    >
      <Accordion items={headerLinks} triggerTextVariant='standard-bold'>
        {accounts &&
          accounts.businessTabName &&
          accounts.personalTabName &&
          accounts.businessCards &&
          accounts.personalCards && (
            <AccountsNavigationMenu
              label={accounts.label}
              businessTabName={accounts.businessTabName}
              personalTabName={accounts.personalTabName}
              businessCards={accounts.businessCards}
              personalCards={accounts.personalCards}
              businessCta={accounts.businessCta}
              cta={accounts.cta}
              isMobile
            />
          )}
      </Accordion>
      <div className='flex flex-col w-screen'>
        <hr className='border-neutral-alternate opacity-30' />
        <div className='flex flex-row justify-around py-32'>
          <SignupButton
            size='small'
            registrationUrl={registrationUrl}
            aria-label={localiseString('signupButton')}
          />
          <LoginButton
            href={bankingUrl}
            variant='secondary'
            aria-label={localiseString('loginButton')}
          />
        </div>
      </div>
    </nav>
  )
}

export const MinimalHeader = ({ banner }: MinimalHeaderProps) => {
  const headerRef = useRef(null)
  const locale = useGetLocale()

  return (
    <header
      ref={headerRef}
      className='z-50 w-full top-0 left-0 bg-neutral sticky'
    >
      {banner && <LandingPageBanner bannerData={banner} />}
      <div className='flex items-center h-header-height lg:h-lg-header-height'>
        <div className='max-w-container w-full m-auto px-16 lg:px-32 flex flex-row justify-between items-center'>
          <LogoLink url={`/${locale || DEFAULT_LOCALE}`} />
          <LocaleSwitcherMenuDropdown excludeCountrySwitcher isOpeningToLeft />
        </div>
      </div>
    </header>
  )
}

export const Header = ({
  headerData,
  bankingUrl,
  registrationUrl,
  pageCtaLink,
  qrCodeCta,
  banner,
  trackingName,
  productPreference,
}: HeaderProps) => {
  const headerRef = useRef(null)
  const navRef = useRef(null)
  const [isNavOpen, setIsNavOpen] = useState(false)
  const toggleNavOpen = () => setIsNavOpen(!isNavOpen)
  const locale = useGetLocale()
  const localiseString = useLocaliseString()
  const matches = useMatches()
  const { pathname } = useLocation()

  // Check if the current page is a blog post individual page to render the progress bar
  const isBlogPostPage = matches.some(match => match.id.includes('blog-post'))

  useEffect(() => {
    if (!navRef.current) return

    if (isNavOpen) {
      disableBodyScroll(navRef.current)
    } else {
      enableBodyScroll(navRef.current)
    }

    return () => clearAllBodyScrollLocks()
  }, [isNavOpen])

  // If there is a change in the pathname, that means a navigation happened.
  // In this case, we should close the navigation menu
  useEffect(() => {
    setIsNavOpen(false)
  }, [pathname])

  return (
    <WebsiteModuleContext.Provider
      value={{ moduleType: 'header', moduleId: 'null' }}
    >
      <header
        ref={headerRef}
        className='z-header w-[100vw] max-w-[100vw] top-0 left-0 bg-neutral sticky'
      >
        {banner && <LandingPageBanner bannerData={banner} />}
        <div className='flex items-center h-header-height lg:h-lg-header-height'>
          <div className='max-w-container w-full mx-auto px-16 lg:px-32 flex flex-row justify-between items-center'>
            <div className='flex flex-row gap-12 items-center'>
              <span className='lg:hidden items-center leading-none'>
                <Hamburger
                  isNavOpen={isNavOpen}
                  toggleNavOpen={toggleNavOpen}
                />
              </span>
              <LogoLink url={`/${locale || DEFAULT_LOCALE}`} />
              <div className='hidden lg:block'>
                <LocaleSwitcherMenuDropdown />
              </div>
              {isNavOpen && (
                <MobileMenu
                  headerRef={headerRef}
                  navRef={navRef}
                  headerData={headerData}
                  bankingUrl={bankingUrl}
                  registrationUrl={registrationUrl}
                />
              )}
            </div>
            <div className='hidden lg:flex lg:flex-row lg:min-w-fit'>
              {headerData.map((item, index: number) =>
                item.links ? (
                  <NavigationMenu
                    label={item.label}
                    links={item.links}
                    key={`${item.label}-${index}}`}
                  />
                ) : (
                  item.businessTabName &&
                  item.personalTabName &&
                  item.businessCards &&
                  item.personalCards && (
                    <AccountsNavigationMenu
                      label={item.label}
                      key={`${item.label}-${index}}`}
                      businessTabName={item.businessTabName}
                      personalTabName={item.personalTabName}
                      businessCards={item.businessCards}
                      personalCards={item.personalCards}
                      businessCta={item.businessCta}
                      cta={item.cta}
                    />
                  )
                )
              )}
            </div>
            <div className='flex flex-row gap-12 items-center min-w-max'>
              <span className='hidden xl:flex'>
                <LoginButton href={bankingUrl} />
              </span>
              <span className='hidden md:flex xl:hidden hover:text-neutral-alternate'>
                <Link
                  url={bankingUrl}
                  onClick={() => handleClick(bankingUrl)}
                  aria-label={localiseString('loginButton')}
                >
                  <IconLogin color='currentColor' />
                </Link>
              </span>
              {/* Mobile only */}
              <SignupButton
                pageCtaLink={pageCtaLink}
                className={`${isNavOpen ? 'hidden' : 'block lg:hidden'}`}
                qrCodeCta={qrCodeCta}
                registrationUrl={registrationUrl}
                size='small'
                aria-label={localiseString('signupButton')}
                trackingName={trackingName}
                productPreference={productPreference}
              />
              {/* Desktop only */}
              <SignupButton
                pageCtaLink={pageCtaLink}
                className='hidden lg:block'
                qrCodeCta={qrCodeCta}
                registrationUrl={registrationUrl}
                size='standard'
                aria-label={localiseString('signupButton')}
                trackingName={trackingName}
                productPreference={productPreference}
              />

              <span className={`${!isNavOpen ? 'hidden' : null} md:flex `}>
                {isNavOpen && <LocaleSwitcherMenuDropdown isOpeningToLeft />}
              </span>
            </div>
          </div>
        </div>
        {isBlogPostPage && <ProgressBar />}
      </header>
    </WebsiteModuleContext.Provider>
  )
}
